import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatINRFull, formatStatus, getStatusColor } from '@/lib/utils';
import { Plus, Check, X, Clock, DollarSign } from 'lucide-react';

const CLAIM_CATEGORIES = ['TRAVEL', 'FOOD', 'ACCOMMODATION', 'LOCAL_TRANSPORT', 'MISC'];
const REGIONS = ['RUDRAPUR', 'DELHI_NCR', 'LUCKNOW', 'DEHRADUN', 'MUMBAI', 'PUNE', 'BANGALORE', 'CHENNAI', 'KOLKATA', 'HYDERABAD', 'OTHER'];

export default function MyTADAPage() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRegion, setFilterRegion] = useState('');

  const fetchClaims = () => {
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (filterRegion) params.set('region', filterRegion);
    api.get(`/expense-claims?${params}`).then(r => setClaims(r.data.claims || [])).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchClaims(); }, [filterStatus, filterRegion]);

  const isSales = user?.role === 'SALES';
  const isOwnOnly = ['SALES', 'SUPERVISOR'].includes(user?.role);
  const canApprove = ['DIRECTOR', 'HR_ADMIN'].includes(user?.role);

  const updateClaim = async (claimId, status) => {
    const action = status === 'APPROVED' ? 'approve' : status === 'REJECTED' ? 'reject' : 'mark as paid';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this claim?`)) return;
    try {
      await api.patch(`/expense-claims/${claimId}`, { status });
      fetchClaims();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  // Stats
  const total = claims.reduce((s, c) => s + (c.amount || 0), 0);
  const pending = claims.filter(c => c.status === 'SUBMITTED').length;
  const approved = claims.filter(c => c.status === 'APPROVED').length;

  return (
    <div data-testid="my-tada-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">{isOwnOnly ? 'My Expenses' : 'Expense Claims'}</h1>
          <p className="text-sm text-slate-500">{claims.length} claims | {formatINRFull(total)} total</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-expense-claim-btn"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> New Claim
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs text-slate-500">Pending</p>
          <p className="text-lg font-semibold text-amber-600" data-testid="pending-count">{pending}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs text-slate-500">Approved</p>
          <p className="text-lg font-semibold text-emerald-600" data-testid="approved-count">{approved}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-xs text-slate-500">Total Amount</p>
          <p className="text-lg font-semibold text-[#1B5486]">{formatINRFull(total)}</p>
        </div>
      </div>

      <div className="flex gap-3">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-claim-status">
          <option value="">All Status</option>
          {['SUBMITTED', 'APPROVED', 'REJECTED', 'PAID'].map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
        </select>
        {canApprove && (
          <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-claim-region">
            <option value="">All Regions</option>
            {REGIONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
          </select>
        )}
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Category</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Purpose / Route</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Region</th>
              {!isOwnOnly && <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Submitted By</th>}
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Amount</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Status</th>
              {canApprove && <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>}
            </tr></thead>
            <tbody>
              {claims.map(c => (
                <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`claim-row-${c.id}`}>
                  <td className="py-2.5 px-4">{formatDate(c.date)}</td>
                  <td className="py-2.5 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(c.category)}</span></td>
                  <td className="py-2.5 px-4 text-slate-700">
                    {c.from_location && c.to_location ? `${c.from_location} → ${c.to_location}` : ''}{c.purpose ? ` | ${c.purpose}` : ''}{c.description || ''}
                  </td>
                  <td className="py-2.5 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{c.region || '-'}</span></td>
                  {!isOwnOnly && <td className="py-2.5 px-4 text-slate-500">{c.user_name || '-'}</td>}
                  <td className="py-2.5 px-4 text-right font-mono font-medium">{formatINRFull(c.amount)}</td>
                  <td className="py-2.5 px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(c.status)}`}>{formatStatus(c.status)}</span></td>
                  {canApprove && (
                    <td className="py-2.5 px-4 text-right">
                      {c.status === 'SUBMITTED' && (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => updateClaim(c.id, 'APPROVED')} data-testid={`approve-claim-${c.id}`}
                            className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 flex items-center gap-0.5">
                            <Check size={12} /> Approve
                          </button>
                          <button onClick={() => updateClaim(c.id, 'REJECTED')} data-testid={`reject-claim-${c.id}`}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-0.5">
                            <X size={12} /> Reject
                          </button>
                        </div>
                      )}
                      {c.status === 'APPROVED' && (
                        <button onClick={() => updateClaim(c.id, 'PAID')} data-testid={`pay-claim-${c.id}`}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-0.5">
                          <DollarSign size={12} /> Mark Paid
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {claims.length === 0 && <div className="text-center py-12 text-slate-400">No expense claims yet. Click "New Claim" to submit.</div>}
        </div>
      )}

      {showCreate && <CreateClaimModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchClaims(); }} />}
    </div>
  );
}

function CreateClaimModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), category: 'TRAVEL', from_location: '', to_location: '', purpose: '', description: '', amount: '', region: '' });
  const [saving, setSaving] = useState(false);
  const REGIONS = ['RUDRAPUR', 'DELHI_NCR', 'LUCKNOW', 'DEHRADUN', 'MUMBAI', 'PUNE', 'BANGALORE', 'CHENNAI', 'KOLKATA', 'HYDERABAD', 'OTHER'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    if (!window.confirm(`Submit expense claim of ${formatINRFull(parseFloat(form.amount))} for ${formatStatus(form.category)}? This will be sent for approval.`)) return;
    setSaving(true);
    try {
      await api.post('/expense-claims', { ...form, amount: parseFloat(form.amount) || 0 });
      onCreated();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-md max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-claim-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">New Expense Claim</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input-field w-full" data-testid="claim-date" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field w-full" data-testid="claim-category">
                {CLAIM_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">From</label>
              <input value={form.from_location} onChange={e => setForm({...form, from_location: e.target.value})} className="input-field w-full" placeholder="City / Location" data-testid="claim-from" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">To</label>
              <input value={form.to_location} onChange={e => setForm({...form, to_location: e.target.value})} className="input-field w-full" placeholder="City / Location" data-testid="claim-to" /></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Purpose</label>
            <input value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} className="input-field w-full" data-testid="claim-purpose" /></div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Region</label>
            <select value={form.region} onChange={e => setForm({...form, region: e.target.value})} className="input-field w-full" data-testid="claim-region">
              <option value="">Select Region</option>
              {REGIONS.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
            </select></div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Amount *</label>
            <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required className="input-field w-full" data-testid="claim-amount" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-claim-btn"
              className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Submitting...' : 'Submit Claim'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
